"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StrategyManagerProgram = void 0;
const anchor_1 = require("@coral-xyz/anchor");
const spl_token_0_4_x_1 = require("@solana/spl-token-0.4.x");
const buffer_1 = require("buffer");
const idls_1 = require("../idls");
const utils_1 = require("../utils");
class StrategyManagerProgram {
    program;
    constructor(programId, provider) {
        this.program = new anchor_1.Program(idls_1.strategyManagerIdl, programId, provider);
    }
    get provider() {
        return this.program.provider;
    }
    get userPublicKey() {
        if (!this.provider.publicKey) {
            throw Error(`Wallet public key not found`);
        }
        return this.provider.publicKey;
    }
    get programId() {
        return this.program.programId;
    }
    // --------------------------------- Pda -------------------------------------
    findStrategyManagerPda() {
        const [statePda] = anchor_1.web3.PublicKey.findProgramAddressSync([buffer_1.Buffer.from('STATE')], this.programId);
        return statePda;
    }
    findStrategyPda(mint) {
        const [strategyPda] = anchor_1.web3.PublicKey.findProgramAddressSync([buffer_1.Buffer.from('STRATEGY'), mint.toBuffer()], this.programId);
        return strategyPda;
    }
    findUserDepositPda(mint, user) {
        const [userDepositPda] = anchor_1.web3.PublicKey.findProgramAddressSync([buffer_1.Buffer.from('DEPOSIT'), user.toBuffer(), mint.toBuffer()], this.programId);
        return userDepositPda;
    }
    // --------------------------------- State ---------------------------------------
    async fetchStrategyManagerState() {
        const pda = this.findStrategyManagerPda();
        const state = await this.program.account.strategyManager.fetch(pda);
        return state.data.v1[0];
    }
    async fetchStrategyState(mint) {
        const pda = this.findStrategyPda(mint);
        const state = await this.program.account.strategy.fetch(pda);
        return state.data.v1[0];
    }
    async fetchUserDepositState(mint, user) {
        const pda = this.findUserDepositPda(mint, user);
        const state = await this.program.account.userDeposit.fetch(pda);
        return state.data.v1[0];
    }
    // ------------------------------ Instruction Accounts -------------------------------
    getRegisterInstructionAccounts(mint, user) {
        const userDepositPda = this.findUserDepositPda(mint, user);
        const strategyPda = this.findStrategyPda(mint);
        return {
            userDeposit: userDepositPda,
            strategy: strategyPda,
            strategyMint: mint,
            signer: user,
        };
    }
    getDepositInstructionAccounts(mint, user) {
        const userAta = (0, spl_token_0_4_x_1.getAssociatedTokenAddressSync)(mint, user);
        const strategyPda = this.findStrategyPda(mint);
        const strategyAta = (0, spl_token_0_4_x_1.getAssociatedTokenAddressSync)(mint, strategyPda, true);
        const userDepositPda = this.findUserDepositPda(mint, user);
        return {
            from: user,
            fromAta: userAta,
            strategyAta,
            userDeposit: userDepositPda,
            strategy: strategyPda,
            strategyMint: mint,
        };
    }
    getQueueWithdrawInstructionAccounts(mint, user) {
        const strategyPda = this.findStrategyPda(mint);
        const userDepositPda = this.findUserDepositPda(mint, user);
        const strategyManagerPda = this.findStrategyManagerPda();
        return {
            receiver: user,
            userDeposit: userDepositPda,
            strategy: strategyPda,
            state: strategyManagerPda,
            strategyMint: mint,
        };
    }
    getCompleteWithdrawInstructionAccounts(mint, user) {
        const strategyManagerPda = this.findStrategyManagerPda();
        const strategyPda = this.findStrategyPda(mint);
        const userDepositPda = this.findUserDepositPda(mint, user);
        const receiverAta = (0, spl_token_0_4_x_1.getAssociatedTokenAddressSync)(mint, user);
        const strategyAta = (0, spl_token_0_4_x_1.getAssociatedTokenAddressSync)(mint, strategyPda, true);
        return {
            receiver: user,
            receiverAta,
            strategyAta,
            userDeposit: userDepositPda,
            strategy: strategyPda,
            state: strategyManagerPda,
            strategyMint: mint,
        };
    }
    // -------------------------------- Estimate Priority Fee ------------------------------
    async estimatePriorityFee(options) {
        try {
            const microLamports = await (0, utils_1.estimatePriorityFee)(this.provider.connection.rpcEndpoint, [this.programId.toBase58()], options);
            return anchor_1.web3.ComputeBudgetProgram.setComputeUnitPrice({
                microLamports,
            });
        }
        catch (e) {
            console.warn(`estimatePriorityFee failed: ${e.message}`);
            return;
        }
    }
    // ------------------------------------- Method ---------------------------------------
    async deposit(mint, amount, options) {
        const user = this.userPublicKey;
        const preInstructions = [];
        try {
            await this.fetchUserDepositState(mint, user);
        }
        catch (e) {
            if (e.message.includes('Account does not exist or has no data')) {
                const accounts = this.getRegisterInstructionAccounts(mint, user);
                const register = await this.program.methods
                    .register()
                    .accounts(accounts)
                    .instruction();
                preInstructions.push(register);
            }
            else {
                throw e;
            }
        }
        const accounts = this.getDepositInstructionAccounts(mint, user);
        const price = await this.estimatePriorityFee(options);
        try {
            await this.program.methods
                .deposit(amount)
                .accounts(accounts)
                .preInstructions(preInstructions)
                .preInstructions(price ? [price] : [])
                .rpc({
                ...(options ?? {}),
                commitment: options?.commitment ?? 'confirmed',
                skipPreflight: options?.skipPreflight ?? true,
                maxRetries: options?.maxRetries ?? 5,
            });
        }
        catch (e) {
            (0, utils_1.throwIncludeErrorMessages)(e);
        }
    }
    async queueWithdraw(mint, amount, options) {
        const accounts = this.getQueueWithdrawInstructionAccounts(mint, this.userPublicKey);
        const price = await this.estimatePriorityFee(options);
        try {
            await this.program.methods
                .queueWithdraw(amount)
                .accounts(accounts)
                .preInstructions(price ? [price] : [])
                .rpc({
                ...(options ?? {}),
                commitment: options?.commitment ?? 'confirmed',
                skipPreflight: options?.skipPreflight ?? true,
                maxRetries: options?.maxRetries ?? 5,
            });
        }
        catch (e) {
            (0, utils_1.throwIncludeErrorMessages)(e);
        }
    }
    async completeWithdraw(mint, options) {
        const accounts = this.getCompleteWithdrawInstructionAccounts(mint, this.userPublicKey);
        const price = await this.estimatePriorityFee(options);
        try {
            await this.program.methods
                .completeWithdraw()
                .accounts(accounts)
                .preInstructions(price ? [price] : [])
                .rpc({
                ...(options ?? {}),
                commitment: options?.commitment ?? 'confirmed',
                skipPreflight: options?.skipPreflight ?? true,
                maxRetries: options?.maxRetries ?? 5,
            });
        }
        catch (e) {
            (0, utils_1.throwIncludeErrorMessages)(e);
        }
    }
    // --------------------------------------- View -------------------------------------
    async getTokenBalanceForOwner(mint, owner) {
        return (0, utils_1.getTokenBalanceForOwner)(this.provider.connection, mint, owner);
    }
    async getTokenBalanceForStrategy(mint) {
        const pda = this.findStrategyPda(mint);
        return this.getTokenBalanceForOwner(mint, pda);
    }
    async getMints() {
        const state = await this.fetchStrategyManagerState();
        return state.strategyMints.slice(0, state.strategyMintsLen);
    }
    async getUserDepositAmount(mint, user) {
        const state = await this.fetchUserDepositState(mint, user);
        return state.deposited;
    }
    async getTotalDepositAmount(mint) {
        const state = await this.fetchStrategyState(mint);
        return state.totalDeposited;
    }
    async getWithdrawAvailableTime(mint, user) {
        const strategyManagerState = await this.fetchStrategyManagerState();
        const strategyState = await this.fetchStrategyState(mint);
        const userDepositState = await this.fetchUserDepositState(mint, user);
        const availableTime = userDepositState.withdrawStartedAt.toNumber() +
            Math.max(strategyManagerState.minWithdrawDelay.toNumber(), strategyState.withdrawDelay.toNumber());
        return availableTime * 1000;
    }
    async getWithdrawDelay(mint) {
        const strategyManagerState = await this.fetchStrategyManagerState();
        const strategyState = await this.fetchStrategyState(mint);
        return Math.max(strategyManagerState.minWithdrawDelay.toNumber(), strategyState.withdrawDelay.toNumber()) * 1000;
    }
    async getQueuedWithdrawAmount(mint, user) {
        const state = await this.fetchUserDepositState(mint, user);
        return state.queuedWithdrawAmount;
    }
}
exports.StrategyManagerProgram = StrategyManagerProgram;
//# sourceMappingURL=StrategyManagerProgram.js.map